import React from 'react';
import { Link } from 'react-router-dom';
import { Card, Table, Badge, Button, EmptyState, LoadingSkeleton } from '../ui';
import { AcademicCapIcon } from '@heroicons/react/24/outline';
import useApi from '../portal/useApi';
import { termLabel } from '../portal/format';
import styles from '../portal/Portal.module.css';

// Home screen for teachers. A class teacher sees their own classes and
// today's lessons; a Head of Academics additionally sees every class.
function TeacherDashboard({ token, user }) {
  const { data, loading, error } = useApi(token, '/api/v1/teacher/me');
  const features = user?.features || {};

  if (loading) return <LoadingSkeleton type="card" rows={3} />;
  if (error) return <div className="alert alert-error">{error}</div>;

  const { teacher, classes, todays_lessons: lessons, all_classes: allClasses, is_head_of_academics: isHead } = data;

  return (
    <div>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Welcome, {user?.full_name}</h2>
          <p className={styles.subtitle}>
            {isHead ? 'Head of Academics' : 'Class teacher'}
            {data.current_term ? ` · ${termLabel(data.current_term)}` : ''}
          </p>
        </div>
        <div className={styles.quickLinks}>
          {features.attendance !== false && <Link to="/attendance"><Button variant="primary" size="sm">Take attendance</Button></Link>}
          {features.exam_results !== false && <Link to="/exams"><Button variant="secondary" size="sm">Enter marks</Button></Link>}
          <Link to="/students"><Button variant="secondary" size="sm">Students</Button></Link>
        </div>
      </div>

      {!teacher && (
        <div className="alert alert-warning">
          Your login is not linked to a teacher record yet, so no classes of your own are shown. Ask the school administrator to link it.
        </div>
      )}

      {teacher && (
        <>
          <h3 className={styles.sectionTitle}>Today's lessons — {data.today}</h3>
          <Card>
            {lessons.length === 0 ? (
              <p className={styles.muted}>No lessons are timetabled for you today.</p>
            ) : (
              <ul className={styles.lessons}>
                {lessons.map((lesson) => (
                  <li key={lesson.id} className={styles.lesson}>
                    <span className={styles.lessonTime}>{lesson.start_time}–{lesson.end_time}</span>
                    <span className={styles.lessonSubject}>{lesson.subject}</span>
                    <span className={styles.lessonMeta}>{lesson.class_name}{lesson.room ? ` · Room ${lesson.room}` : ''}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <h3 className={styles.sectionTitle}>My classes</h3>
          {classes.length === 0 ? (
            <Card>
              <EmptyState
                title="No classes assigned yet"
                message="Classes appear here once you are made a homeroom teacher or allocated a subject."
                icon={<AcademicCapIcon />}
              />
            </Card>
          ) : (
            <div className={styles.grid}>
              {classes.map((schoolClass) => (
                <div key={schoolClass.id} className={styles.tile}>
                  <div className={styles.tileHead}>
                    <div className={styles.tileName}>{schoolClass.display_name}</div>
                    {schoolClass.is_homeroom && <Badge variant="primary">Homeroom</Badge>}
                  </div>
                  <div className={styles.tileMeta}>
                    {schoolClass.student_count} {schoolClass.student_count === 1 ? 'student' : 'students'}
                  </div>
                  <div className={styles.tileMeta}>
                    {schoolClass.subjects.length ? schoolClass.subjects.join(', ') : 'No subjects allocated this term'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {allClasses && (
        <>
          <h3 className={styles.sectionTitle}>All classes</h3>
          <Card>
            <Table
              columns={[
                { header: 'Class', render: (row) => <strong>{row.display_name}</strong> },
                { header: 'Homeroom teacher', render: (row) => row.homeroom_teacher_name || '—' },
                { header: 'Students', accessor: 'student_count' },
              ]}
              data={allClasses}
              emptyMessage="No classes have been set up yet."
            />
          </Card>
        </>
      )}
    </div>
  );
}

export default TeacherDashboard;
